/**
 * What one frame sounds like: the per-frame engine/gun/wind update handed to
 * `audio/engine.ts`.
 *
 * Split out of `flight-scene.ts` for the reason `scene-draw.ts`,
 * `scene-boot.ts`, `scene-particles.ts` and the rest of the siblings were —
 * the host sits at aislop's 400-line ceiling, and this is a cohesive block with
 * one caller and no state of its own: it reads a settled sim tick and pushes a
 * mixer update, and nothing in the frame loop reads it back.
 *
 * The host is described **structurally** rather than by importing `SceneState`,
 * which stays private to `flight-scene.ts` — the same arrangement every other
 * sibling here uses.
 */

import type { FlightInputs, SimState } from "@red-opus/engine";
import type { FlightAudioEngine } from "../audio/engine.ts";
import { gunAudioFiring } from "./frame-signals.ts";

export interface FrameAudioInput {
  readonly dt: number;
  readonly inputs: FlightInputs;
  readonly simState: SimState;
  /** From `sunSuppressedCombat` — whether the player's own aeroplane is alight. */
  readonly burning: boolean;
  readonly nearestEnemyRangeM: number | null;
  readonly flakBurstDistancesM: readonly number[];
  /** From `firingOtherAircraftRangesM` — how far away each aeroplane that is *not* the player and whose guns fired this frame is (G36). */
  readonly firingAircraftRangesM: readonly number[];
  /**
   * `EffectsSettings.enhancedEffects`, straight off `flight-scene.ts`'s live
   * `effects` object. The only sound in the port that rides a DISPLAY row: the
   * dive whine (`audio/dive-whine.ts`) is a declared invention, so it is gated
   * by the same switch every other "the original did not do this" affordance is.
   *
   * Passed **per frame** rather than through `setEffectsSettings`, for the
   * reason `scene-boot.ts` gives for 3D COCKPIT needing nothing there: reading
   * the live value every frame means flipping the row takes effect on the next
   * frame, with no reload and nothing to invalidate — and here it also means the
   * layer *fades* across the change instead of cutting, because the gain it
   * drives is smoothed.
   */
  readonly enhancedEffects: boolean;
}

/**
 * The three reasons a flight goes quiet, as `flight-scene.ts` holds them.
 * Structural, like `FrameAudioInput` above — `SceneState` stays private.
 */
export interface FlightSilence {
  /** What the route last asked for: P, the map sheet, or the PREFERENCES overlay, already one boolean (`Fly.tsx`'s `pauseWanted`). */
  readonly paused: boolean;
  /** Latched the moment a flown `MissionSpec` is decided and the debriefing goes up (`mission-report.ts`). */
  readonly missionEnded: boolean;
  /** The instant dogfight's own equivalent — its bare encounter is over. */
  readonly dogfightEnded: boolean;
}

/**
 * Whether the mix should be gated shut this frame — the sim has stopped, so
 * nothing sounding off it means anything any more (`audio/pause-gate.ts`).
 *
 * Two kinds of reason, and the second is why this is a per-frame question
 * rather than something `setPaused` could answer alone. **Paused** is the
 * deliberate one, and somebody pressed a key for it. **Ended** is the one
 * nobody presses: a decided mission or dogfight freezes its own runner
 * (`sim-runner.ts`'s `advance`), and until this existed the frozen state kept
 * sounding under the debriefing — the engine droning on behind the score card
 * and, if the aeroplane went down alight, the fire loop burning for ever over
 * the death screen, because the gate it rides was left open by the last tick
 * that ever ran.
 *
 * Nothing here freezes the *sim*: it already is frozen, by the runner itself,
 * on the tick the outcome was decided. Gating the mix off the same two latches
 * the debriefing screen is drawn from is what keeps picture and sound agreeing
 * about when the flight is over, and cannot disturb the recorded outcome.
 */
export function silentFlight(state: FlightSilence): boolean {
  return state.paused || state.missionEnded || state.dogfightEnded;
}

export function updateFrameAudio(audio: FlightAudioEngine, frame: FrameAudioInput): void {
  const { simState, inputs } = frame;
  audio.updateFrame(frame.dt, {
    throttle: simState.engine.throttle,
    engineHealth: simState.damage.engine,
    airspeedMps: simState.readout.airspeedMps,
    burning: frame.burning,
    // Rounds actually leaving the guns (`gunAudioFiring`, `frame-signals.ts`) —
    // empty, jammed, and dead-stick trigger cases all read false here and get
    // the dead-trigger click instead, via `triggerDown`/`gunsJammed` below.
    firing: gunAudioFiring(inputs.firing, simState.guns, simState.pilot),
    triggerDown: inputs.firing,
    gunsJammed: simState.guns.jammed,
    stalled: simState.readout.stalled,
    nearestEnemyRangeM: frame.nearestEnemyRangeM,
    flakBurstDistancesM: frame.flakBurstDistancesM,
    firingAircraftRangesM: frame.firingAircraftRangesM,
    enhancedEffects: frame.enhancedEffects,
  });
}
